import { StatusCodes } from "http-status-codes";
import { IPD_BILLING_APIS } from "../utils/constants";
import {
  IIPDBill,
  IIPDBillListFetchResp,
  IIPDBillMapping,
  IIPDBillMappingFetchResp,
  IIPDDoc,
  IIPDDocS3Data,
  IIPDErrorResp,
  IIPDHealthCareBillHeader,
  IIPDHealthCareBillHeaderFetchResp,
  IIPDHealthCareIPDBillItem,
  IIPDHealthCareBillMedicalItem,
  IIPDManualQuestion,
  IIPDPatientPayable,
  IIPDPatientPayableFetchResp,
  IIPDSchemeBillHeader,
  IIPDSchemeBillImplantItem,
  IIPDSchemeIPDBillItem,
  IIPDSchemeBillMedicalItem,
  IIPDSchemeBillPackageItem,
  IIPDSchemeBillPatientPayableItem,
  IIPDSchemeHeaderFetchResp,
  IIPDUploadedDocData,
  IIPDAnsweredQuestion,
  IIPDFetchQuestionAnswersResp,
  IIPDPostQuestionAnswerState,
  IIPDSchemeHeaderUpdate,
  IIPDListFilters,
} from "../interfaces/IIPDBills";
import Axios from "../utils/axiosInterceptor";

export const fetchIPDBills = async (
  hospitalId: number,
  filters: IIPDListFilters
): Promise<IIPDBillListFetchResp> => {
  try {
    const resp = await Axios.post<{
      data: IIPDBillListFetchResp;
      status: number;
    }>(IPD_BILLING_APIS.GET_IPD_BILL_LIST(hospitalId), {
      ...filters,
      pageNumber: filters.pageNumber - 1,
    });
    if (resp.status === StatusCodes.OK) {
      return resp.data;
    } else {
      throw new Error("Cannot fetch ipd bills list");
    }
  } catch (error) {
    throw new Error("Cannot fetch ipd bills list");
  }
};

export const fetchIPDQuestionnaires = async (
  accessToken: string
): Promise<{
  ipdQuestionnaires?: Array<IIPDManualQuestion>;
  errorMessage?: string;
  status: number;
}> => {
  try {
    const response = await fetch(IPD_BILLING_APIS.IPD_QUESTIONNAIRES, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    });
    const ipdQuestionnaires: Array<IIPDManualQuestion> = await response.json();
    if (response.status === StatusCodes.OK) {
      return { ipdQuestionnaires, status: response.status };
    } else {
      return {
        errorMessage: "Cannot fetch ipd questionnaires list",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot fetch ipd questionnaires list");
  }
};

export const uploadIPDDocToServer = async (
  fileObj: object,
  accessToken: string,
  docType: string,
  patientId: string | null,
  caseId: string | null,
  hospitalId: string | null,
  visitId: string | null
): Promise<{
  docUploadResp?: IIPDDoc;
  status: number;
  errorMessage?: string;
  docType: string;
}> => {
  try {
    const formData = new FormData();
    formData.append("file", fileObj as Blob);
    formData.append("hospitalId", hospitalId ?? "");
    formData.append("patientId", patientId ?? "");
    formData.append("caseId", caseId ?? "");
    formData.append("documentType", docType);
    formData.append("visitId", visitId ?? "");

    const response = await fetch(IPD_BILLING_APIS.UPLOAD_DOC, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    });
    const docUploadResp: IIPDDoc = await response.json();
    if (response.status === StatusCodes.OK) {
      return { docUploadResp, status: response.status, docType };
    } else {
      return {
        errorMessage: "Cannot upload document",
        status: response.status,
        docType,
      };
    }
  } catch (error) {
    throw new Error("Cannot upload document");
  }
};

export const getNewIPDBillMapping = async (
  accessToken: string,
  hospitalId: number,
  documentPaths: Array<IIPDDocS3Data>,
  billMapping: object
): Promise<IIPDBillMappingFetchResp | IIPDErrorResp> => {
  try {
    const response = await fetch(
      IPD_BILLING_APIS.GET_BILL_MAPPING(hospitalId),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ billMapping, documentPaths }),
      }
    );
    const billMappingResp: {
      billMapping: IIPDBillMapping;
      documentPaths: Array<IIPDUploadedDocData>;
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: billMappingResp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get new ipd bill",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get new ipd bill");
  }
};

export const getNewIPDBillHealthCareBillHeader = async (
  accessToken: string,
  healthCareBillHeaderId: number
): Promise<IIPDHealthCareBillHeaderFetchResp | IIPDErrorResp> => {
  try {
    const response = await fetch(
      IPD_BILLING_APIS.GET_HEALTHCARE_BILL_HEADER(healthCareBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: {
      healthCareBillHeader: IIPDHealthCareBillHeader;
      healthCareBillItems: {
        healthCareBillIpdItems: Array<IIPDHealthCareIPDBillItem>;
        healthCareBillMedicalItems: Array<IIPDHealthCareBillMedicalItem>;
      };
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: resp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get healthcare bill headers",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get healthcare bill headers");
  }
};

export const getHealthCarePatientPayable = async (
  accessToken: string,
  healthCareBillHeaderId: number
): Promise<IIPDPatientPayableFetchResp | IIPDErrorResp> => {
  try {
    const response = await fetch(
      IPD_BILLING_APIS.GET_PATIENT_PAYABLE(healthCareBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: IIPDPatientPayable = await response.json();
    if (response.status === StatusCodes.OK) {
      return {
        response: {
          data: resp,
          healthCareBillHeaderId: healthCareBillHeaderId,
        },
        status: response.status,
      };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get healthcare patient payable",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get healthcare patient payable");
  }
};

export const getIPDQuestionAnswerCollection = async (
  accessToken: string,
  billMappingId: number
): Promise<IIPDFetchQuestionAnswersResp | IIPDErrorResp> => {
  try {
    const response = await fetch(
      IPD_BILLING_APIS.GET_QUESTION_ANSWER_COLLECTION(billMappingId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: IIPDPostQuestionAnswerState = await response.json();
    if (response.status === StatusCodes.OK) {
      return {
        response: resp,
        status: response.status,
      };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get ipd question answer collection",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get ipd question answer collection");
  }
};

export const getIPDBillSchemeHeader = async (
  accessToken: string,
  schemeBillHeaderId: number
): Promise<any> => {
  try {
    const response = await fetch(
      IPD_BILLING_APIS.GET_SCHEME_BILL_HEADER(schemeBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: {
      schemeBillHeader: IIPDSchemeBillHeader;
      schemeBillItems: {
        ipdBillImplantItems: Array<IIPDSchemeBillImplantItem>;
        ipdBillPackageItems: Array<IIPDSchemeBillPackageItem>;
        schemeBillIpdItems: Array<IIPDSchemeIPDBillItem>;
        schemeBillIpdItemsPatientPayable: Array<IIPDSchemeBillPatientPayableItem>;
        schemeBillMedicalItems: Array<IIPDSchemeBillMedicalItem>;
        schemeBillMedicalItemsPatientPayable: Array<object>;
        collectionsList: Array<string>;
      };
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: resp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get ipd scheme bill header",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get ipd scheme bill header");
  }
};

export const postQuestionAnswersCollection = async (
  formData: Array<IIPDAnsweredQuestion>
): Promise<IIPDFetchQuestionAnswersResp | IIPDErrorResp> => {
  try {
    const response = await Axios.post(
      IPD_BILLING_APIS.POST_QUESTION_ANSWER_COLLECTION,
      formData
    );

    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot post ipd questions and answers",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot post ipd questions and answers");
  }
};

export const resetIPDSchemeBillItem = async (
  accessToken: string,
  schemeBillHeaderId: number | null
): Promise<any> => {
  try {
    const response = await fetch(
      IPD_BILLING_APIS.RESET_IPD_SCHEME_BILL(schemeBillHeaderId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const resp: {
      schemeBillHeader: IIPDSchemeBillHeader;
      schemeBillItems: {
        ipdBillImplantItems: Array<IIPDSchemeBillImplantItem>;
        ipdBillPackageItems: Array<IIPDSchemeBillPackageItem>;
        schemeBillIpdItems: Array<IIPDSchemeIPDBillItem>;
        schemeBillIpdItemsPatientPayable: Array<IIPDSchemeBillPatientPayableItem>;
        schemeBillMedicalItems: Array<IIPDSchemeBillMedicalItem>;
        schemeBillMedicalItemsPatientPayable: Array<object>;
        collectionsList: Array<string>;
      };
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: resp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get ipd scheme bill header",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get ipd scheme bill header");
  }
};

export const getEditIPDBillMapping = async (
  accessToken: string,
  hospitalId: number,
  ipdBillId: string
): Promise<IIPDBillMappingFetchResp | IIPDErrorResp> => {
  try {
    const response = await fetch(
      IPD_BILLING_APIS.GET_EDIT_IPD_BILL_MAPPING(hospitalId, ipdBillId),
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );
    const billMappingResp: {
      billMapping: IIPDBillMapping;
      documentPaths: Array<IIPDUploadedDocData>;
    } = await response.json();
    if (response.status === StatusCodes.OK) {
      return { response: billMappingResp, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot get edit ipd bill",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot get edit ipd bill");
  }
};

export const updateSchemeBillItems = async (
  hospitalId: number | undefined,
  billMappingId: number | null,
  schemeBillHeaderId: number | null,
  schemeBillData: IIPDSchemeHeaderUpdate
): Promise<IIPDSchemeHeaderFetchResp | IIPDErrorResp> => {
  try {
    const response = await Axios.put(
      IPD_BILLING_APIS.UPDATE_SCHEME_BILL_ITEMS(
        hospitalId,
        billMappingId,
        schemeBillHeaderId
      ),
      schemeBillData
    );
    if (response.status === StatusCodes.CREATED) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot update scheme bill items",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot update scheme bill items");
  }
};

export const callApproveIPDBill = async (
  hospitalId: number | undefined,
  currBillMappingId: number | undefined,
  currHealthCareBillHeaderId: number | undefined,
  currSchemeBillHeaderId: number | undefined,
  status: string
): Promise<{ response: string; status: number } | IIPDErrorResp> => {
  try {
    const url = IPD_BILLING_APIS.APPROVE_IPD_BILL(
      hospitalId,
      currBillMappingId,
      currHealthCareBillHeaderId,
      currSchemeBillHeaderId
    );
    const response = await Axios.get(url, {
      params: { status },
    });
    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot approve IPD bill",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot approve IPD bill");
  }
};

export const downloadAbstractExcel = async (
  currSchemeBillHeaderId: number | undefined
): Promise<{ response: { s3Url: string }; status: number } | IIPDErrorResp> => {
  try {
    const response = await Axios.get(
      IPD_BILLING_APIS.DOWNLOAD_ABSTRACT_EXCEL(currSchemeBillHeaderId)
    );
    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot download abstract excel",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot download abstract excel");
  }
};

export const downloadAbstractZip = async (
  hospitalId: number | undefined,
  dateFrom: string | undefined,
  dateTo: string | undefined
): Promise<{ response: { s3Url: string }; status: number } | IIPDErrorResp> => {
  try {
    const response = await Axios.get(
      IPD_BILLING_APIS.DOWNLOAD_ABSTRACT_ZIP(hospitalId, dateFrom, dateTo)
    );
    if (response.status === StatusCodes.OK) {
      return { response: response.data, status: response.status };
    } else {
      return {
        response: null,
        errorMessage: "Cannot download abstract zip",
        status: response.status,
      };
    }
  } catch (error) {
    throw new Error("Cannot download abstract zip");
  }
};
